
import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, Input, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApprovalState, Client, FindingDescriptionTabDto, ReportType } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { getErrorMessage } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { catchError, combineLatest, EMPTY, filter, Observable, of, switchMap, tap } from 'rxjs';
import { FormValidationService, ReportFormPage, ReportsBulkSaveService, ReportTask } from '../../../../services';
import { ReportForm } from '../../report-form';
import { createEditorConfigs } from '../../shared/richtext-editor-config-helper';

@Component({
  selector: 'app-finding-description-tab',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, AngularEditorModule, FormsModule],
  templateUrl: './finding-description-tab.component.html',
  styleUrl: './finding-description-tab.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FindingDescriptionTabComponent extends ReportForm implements OnInit {

  @Input() data: any = {};
  @Input() readonly: boolean = false;

  public form!: FormGroup;
  public findingDescriptionTabForm!: FormGroup;


  private editorConfigs = createEditorConfigs(18, 'finding-desc');
  configDescEn: AngularEditorConfig = this.editorConfigs[0];
  configDescRu: AngularEditorConfig = this.editorConfigs[1];
  configDescKz: AngularEditorConfig = this.editorConfigs[2];
  configRiskEn: AngularEditorConfig = this.editorConfigs[3];
  configRiskRu: AngularEditorConfig = this.editorConfigs[4];
  configRiskKz: AngularEditorConfig = this.editorConfigs[5];
  configSafeguardsEn: AngularEditorConfig = this.editorConfigs[6];
  configSafeguardsRu: AngularEditorConfig = this.editorConfigs[7];
  configSafeguardsKz: AngularEditorConfig = this.editorConfigs[8];
  configConcernsEn: AngularEditorConfig = this.editorConfigs[9];
  configConcernsRu: AngularEditorConfig = this.editorConfigs[10];
  configConcernsKz: AngularEditorConfig = this.editorConfigs[11];
  configResolutionEn: AngularEditorConfig = this.editorConfigs[12];
  configResolutionRu: AngularEditorConfig = this.editorConfigs[13];
  configResolutionKz: AngularEditorConfig = this.editorConfigs[14];
  configMitigationEn: AngularEditorConfig = this.editorConfigs[15];
  configMitigationRu: AngularEditorConfig = this.editorConfigs[16];
  configMitigationKz: AngularEditorConfig = this.editorConfigs[17];



  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiClient = inject(Client);
  private toastrService = inject(ToastrService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _formValidationService = inject(FormValidationService);

  needMitigationOptions: any[] = [{ value: true, viewValue: 'Yes' }, { value: false, viewValue: 'No' }];

  public isIdrRdrBlockVisible: Signal<boolean>;
  public isItrBlockVisible: Signal<boolean>;
  public showRecommendedMitigation: boolean = false;

  private _reportType = toSignal(this._store.reportType$);
  private _findingDescription = toSignal(this._store.findingDescription$);
  private _general = toSignal(this._store.general$);
  private _closeReport = toSignal(this._store.closeReport$);
  private currentStep = toSignal(this._store.page$);

  private generalTabId!: string;
  private findingDescriptionTabId!: number;

  public get step() {
    return ReportFormPage.FINDING_DESCRIPTION;
  }
  constructor() {
    super();
    this.initializeForm();
    this.isIdrRdrBlockVisible = computed(() => {
      const reportType = this._reportType();
      return reportType === ReportType.IntegrityDeficiency || reportType === ReportType.ReliabilityDeficiency;
    });

    this.isItrBlockVisible = computed(() => {
      const reportType = this._reportType();
      return reportType === ReportType.IntegrityThreat;
    });
  }
  private _destroyRef = inject(DestroyRef);

  protected override onPolicyOrFormStateChanged() {
    this.richTextFieldPolicy();
  }
  richTextFieldPolicy() {
    this.configDescEn.editable = this.isFieldEditable('descriptionOfConditionEn');
    this.configDescRu.editable = this.isFieldEditable('descriptionOfConditionRu');
    this.configDescKz.editable = this.isFieldEditable('descriptionOfConditionKz');
    this.configRiskEn.editable = this.isFieldEditable('riskScenarioDescriptionEn');
    this.configRiskRu.editable = this.isFieldEditable('riskScenarioDescriptionRu');
    this.configRiskKz.editable = this.isFieldEditable('riskScenarioDescriptionKz');
    this.configSafeguardsEn.editable = this.isFieldEditable('implementedSafeguardsDescriptionEn');
    this.configSafeguardsRu.editable = this.isFieldEditable('implementedSafeguardsDescriptionRu');
    this.configSafeguardsKz.editable = this.isFieldEditable('implementedSafeguardsDescriptionKz');
    this.configConcernsEn.editable = this.isFieldEditable('concernsEn');
    this.configConcernsRu.editable = this.isFieldEditable('concernsRu');
    this.configConcernsKz.editable = this.isFieldEditable('concernsKz');
    this.configResolutionEn.editable = this.isFieldEditable('recomendedResolutionEn');
    this.configResolutionRu.editable = this.isFieldEditable('recomendedResolutionRu');
    this.configResolutionKz.editable = this.isFieldEditable('recomendedResolutionKz');
    this.configMitigationEn.editable = this.isFieldEditable('recomendedMitigationEn');
    this.configMitigationRu.editable = this.isFieldEditable('recomendedMitigationRu');
    this.configMitigationKz.editable = this.isFieldEditable('recomendedMitigationKz');
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setRequiredValidatorsByReportType();
    this._store.reportType$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.setRequiredValidatorsByReportType();
      });
    this._initSaveTriggers();

    this.findingDescriptionTabForm.get('needMitigation')?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        this.needMitigationChanged(value);
      });
  }
  needMitigationChanged(value: boolean) {
    this.showRecommendedMitigation = value;
    this.findingDescriptionTabForm.get('recomendedMitigationEn')?.removeValidators(Validators.required);
    if (value) {
      this.findingDescriptionTabForm.get('recomendedMitigationEn')?.addValidators(Validators.required);
    }
    this.findingDescriptionTabForm.get('recomendedMitigationEn')?.updateValueAndValidity()
  }


  private _initSaveTriggers(): void {
    this._store.page$
      .pipe(
        filter((page) => page === this.step),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._store.setPageValidity({ page: this.step, isStepValid: this.form.valid });
      });

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        const isFormValid = this.form.valid;

        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this._save()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });
      });

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        filter(() => !this.findingDescriptionTabForm.dirty),
        switchMap(([params, _]) => {
          const id = params.get('id');
          if (!id) {
            return EMPTY;
          }
          this.generalTabId = id;

          const storedData = this._findingDescription();

          if (storedData && storedData.generalTabId === id) {
            this.findingDescriptionTabForm.patchValue(storedData, { emitEvent: false });

            if (storedData.id) {
              this.findingDescriptionTabId = storedData.id;
            }

            return EMPTY;
          }

          return this._getFindingDescription();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _getFindingDescription() {
    return this.generalTabId
      ? this.apiClient.byGeneralTab3(this.generalTabId).pipe(
        tap((data) => {
          if (data) {
            this.findingDescriptionTabForm.patchValue(data, { emitEvent: false });
            this._store.setFindingDescription(data);

            if (data.id) {
              this.findingDescriptionTabId = data.id;
              this.needMitigationChanged(data?.needMitigation ?? false);
            }
          }
        }),
        catchError((err) => {
          if (err.status === 404) {
            return of(null);
          }

          return of(null);
        })
      )
      : of(null);
  }

  private _save() {
    return this._handleSave().pipe(
      switchMap(() => this._getFindingDescription())
    );
  }

  private _handleSave(): Observable<any> {
    if (this.form.invalid) {
      return of(null);
    }

    const values = this.form.value;

    const handleError = (error: any) => {
      this.toastrService.error(`${error.message}`);
      return of(null);
    };

    const payload = new FindingDescriptionTabDto({
      ...values,
      id: this.findingDescriptionTabId ?? undefined,
      generalTabId: this.generalTabId,
    });

    return this.findingDescriptionTabId
      ? this.apiClient.findingDescriptionTabPUT(this.findingDescriptionTabId, payload)
        .pipe(
          tap((res) => {
            if (!this.findingDescriptionTabId && res) {
              this.findingDescriptionTabId = res;
            }

            if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
              this.toastrService.success('Finding Description updated successfully');
            }
          }),
          catchError(handleError)
        )
      : this.apiClient.findingDescriptionTabPOST(payload)
        .pipe(
          tap((res) => {
            if (!this.findingDescriptionTabId && res) {
              this.findingDescriptionTabId = +res;
            }

            this.toastrService.success('Finding Description saved successfully');
          }),
          catchError(handleError)
        );
  }

  initializeForm(): void {
    this.findingDescriptionTabForm = this.fb.group({
      descriptionOfConditionEn: ['', [Validators.maxLength(20000)]],
      descriptionOfConditionRu: ['', [Validators.maxLength(20000)]],
      descriptionOfConditionKz: ['', [Validators.maxLength(20000)]],
      riskScenarioDescriptionEn: ['', [Validators.maxLength(20000)]],
      riskScenarioDescriptionRu: ['', [Validators.maxLength(20000)]],
      riskScenarioDescriptionKz: ['', [Validators.maxLength(20000)]],
      implementedSafeguardsDescriptionEn: ['', [Validators.maxLength(20000)]],
      implementedSafeguardsDescriptionRu: ['', [Validators.maxLength(20000)]],
      implementedSafeguardsDescriptionKz: ['', [Validators.maxLength(20000)]],
      concernsEn: ['', [Validators.maxLength(20000)]],
      concernsRu: ['', [Validators.maxLength(20000)]],
      concernsKz: ['', [Validators.maxLength(20000)]],
      recomendedResolutionEn: ['', [Validators.maxLength(20000)]],
      recomendedResolutionRu: ['', [Validators.maxLength(20000)]],
      recomendedResolutionKz: ['', [Validators.maxLength(20000)]],
      recomendedMitigationEn: ['', [Validators.maxLength(20000)]],
      recomendedMitigationRu: ['', [Validators.maxLength(20000)]],
      recomendedMitigationKz: ['', [Validators.maxLength(20000)]],
      assetShutdown: [false],
      needMitigation: [false]
    });

    this.form = this.findingDescriptionTabForm;
    this._formValidationService.registerForm(ReportFormPage.FINDING_DESCRIPTION, this.form);
  }

  private setRequiredValidatorsByReportType(): void {
    const reportType = this._reportType();
    this.findingDescriptionTabForm.get('concernsEn')?.removeValidators(Validators.required);
    this.findingDescriptionTabForm.get('recomendedResolutionEn')?.removeValidators(Validators.required);
    this.findingDescriptionTabForm.get('descriptionOfConditionEn')?.removeValidators(Validators.required);
    this.findingDescriptionTabForm.get('riskScenarioDescriptionEn')?.removeValidators(Validators.required);
    this.findingDescriptionTabForm.get('implementedSafeguardsDescriptionEn')?.removeValidators(Validators.required);

    if (reportType === ReportType.IntegrityThreat) {
      this.findingDescriptionTabForm.get('concernsEn')?.addValidators(Validators.required);
      this.findingDescriptionTabForm.get('recomendedResolutionEn')?.addValidators(Validators.required);
    } else if (
      reportType === ReportType.IntegrityDeficiency || reportType === ReportType.ReliabilityDeficiency
    ) {
      this.findingDescriptionTabForm.get('descriptionOfConditionEn')?.addValidators(Validators.required);
      this.findingDescriptionTabForm.get('riskScenarioDescriptionEn')?.addValidators(Validators.required);
      this.findingDescriptionTabForm.get('implementedSafeguardsDescriptionEn')?.addValidators(Validators.required);
    }

    this.findingDescriptionTabForm.get('concernsEn')?.updateValueAndValidity();
    this.findingDescriptionTabForm.get('recomendedResolutionEn')?.updateValueAndValidity();
    this.findingDescriptionTabForm.get('descriptionOfConditionEn')?.updateValueAndValidity();
    this.findingDescriptionTabForm.get('riskScenarioDescriptionEn')?.updateValueAndValidity();
    this.findingDescriptionTabForm.get('implementedSafeguardsDescriptionEn')?.updateValueAndValidity();
  }

  displayNames: { [key: string]: string } = {
    descriptionOfConditionEn: 'Description of Condition [EN]',
    descriptionOfConditionRu: 'Description of Condition [RU]',
    descriptionOfConditionKz: 'Description of Condition [KZ]',
    riskScenarioDescriptionEn: 'Risk Scenario Description [EN]',
    riskScenarioDescriptionRu: 'Risk Scenario Description [RU]',
    riskScenarioDescriptionKz: 'Risk Scenario Description [KZ]',
    implementedSafeguardsDescriptionEn: 'Implemented Safeguards Description [EN]',
    implementedSafeguardsDescriptionRu: 'Implemented Safeguards Description [RU]',
    implementedSafeguardsDescriptionKz: 'Implemented Safeguards Description [KZ]',
    concernsEn: 'Concerns [EN]',
    concernsRu: 'Concerns [RU]',
    concernsKz: 'Concerns [KZ]',
    recomendedResolutionEn: 'Recommended Resolution [EN]',
    recomendedResolutionRu: 'Recommended Resolution [RU]',
    recomendedResolutionKz: 'Recommended Resolution [KZ]',
    recomendedMitigationEn: 'Recommended Mitigation [EN]',
    recomendedMitigationRu: 'Recommended Mitigation [RU]',
    recomendedMitigationKz: 'Recommended Mitigation [KZ]',
    needMitigation: 'Need Mitigation?',
  };

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(this.findingDescriptionTabForm, fieldName, this.displayNames);
  }
  isRequired(fieldName: string): boolean {
    const control = this.form.get(fieldName);

    if (!control) {
      return false;
    }

    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      if (validator && validator['required']) {
        return true;
      }
    }
    return false;
  }
}
